/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

export function useSupabaseData(session) {
  const uid = session?.user?.id;
  const [properties,  setProperties]  = useState([]);
  const [tenants,     setTenants]     = useState([]);
  const [leases,      setLeases]      = useState([]);
  const [payments,    setPayments]    = useState([]);
  const [mxRequests,  setMxRequests]  = useState([]);
  const [messages,    setMessages]    = useState([]);
  const [documents,   setDocuments]   = useState([]);
  const [expenses,    setExpenses]    = useState([]);
  const [vacancies,   setVacancies]   = useState([]);
  const [lateFees,    setLateFees]    = useState([]);
  const [checklists,  setChecklists]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  // Tier is sourced from the subscriptions table — never from client state.
  const [tier,        setTier]        = useState('starter');

  const loadSubscription = useCallback(async () => {
    if (!uid) return;
    const { data, error } = await supabase
      .from('subscriptions')
      .select('tier')
      .eq('user_id', uid)
      .single();
    if (!error && data?.tier) setTier(data.tier);
  }, [uid]);

  const loadAll = useCallback(async () => {
    if (!uid) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, t, l, pay, mx, msg, doc, exp, vac, lf, chk] = await Promise.all([
        supabase.from('properties').select('*, improvements(*), maintenance_schedule(*)').eq('landlord_id', uid),
        supabase.from('tenants').select('*').eq('landlord_id', uid).order('full_name'),
        supabase.from('leases').select('*').eq('landlord_id', uid),
        supabase.from('payments').select('*').eq('landlord_id', uid).order('payment_date', { ascending: false }),
        supabase.from('maintenance_requests').select('*, maintenance_updates(*)').eq('landlord_id', uid).order('created_at', { ascending: false }),
        supabase.from('messages').select('*').eq('landlord_id', uid).order('sent_at', { ascending: false }),
        supabase.from('documents').select('*').eq('landlord_id', uid),
        supabase.from('expenses').select('*').eq('landlord_id', uid).order('expense_date', { ascending: false }),
        supabase.from('vacancies').select('*').eq('landlord_id', uid),
        supabase.from('late_fees').select('*').eq('landlord_id', uid).order('charged_at', { ascending: false }),
        supabase.from('checklists').select('*').eq('landlord_id', uid).order('created_at', { ascending: false }),
      ]);
      setProperties(p.data || []);
      setTenants(t.data || []);
      setLeases(l.data || []);
      setPayments(pay.data || []);
      setMxRequests(mx.data || []);
      setMessages(msg.data || []);
      setDocuments(doc.data || []);
      setExpenses(exp.data || []);
      setVacancies(vac.data || []);
      setLateFees(lf.data || []);
      setChecklists(chk.data || []);
    } catch(e) { console.error('DB load error:', e); }
    finally { setLoading(false); }
  }, [uid]);

  useEffect(() => {
    loadSubscription();
    loadAll();
  }, [loadSubscription, loadAll]);

  // Called by PricingPage when a user selects a plan.
  // In production replace this body with a Stripe Checkout redirect.
  // The tier column must only be writable by the service role once Stripe is live;
  // the "dev_user_updates_own_subscription" policy in 002_subscriptions.sql
  // permits this update during development.
  const updateSubscription = async (newTier) => {
    if (!uid) return { error: { message: 'Not authenticated' } };
    const { data, error } = await supabase
      .from('subscriptions')
      .update({ tier: newTier })
      .eq('user_id', uid)
      .select('tier')
      .single();
    if (!error && data?.tier) setTier(data.tier);
    return { data, error };
  };

  // Properties
  const addProperty    = async d => { const r = await supabase.from('properties').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setProperties(p=>[...p, r.data]); return r; };
  const updateProperty = async (id,d) => { const r = await supabase.from('properties').update(d).eq('id',id).select().single(); if(!r.error) setProperties(p=>p.map(x=>x.id===id?r.data:x)); return r; };

  // Tenants
  const addTenant    = async d => { const r = await supabase.from('tenants').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setTenants(t=>[...t, r.data]); return r; };
  const updateTenant = async (id,d) => { const r = await supabase.from('tenants').update(d).eq('id',id).select().single(); if(!r.error) setTenants(t=>t.map(x=>x.id===id?r.data:x)); return r; };

  // Leases
  const addLease = async d => { const r = await supabase.from('leases').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setLeases(l=>[...l, r.data]); return r; };

  // Payments
  const addPayment = async d => { const r = await supabase.from('payments').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setPayments(p=>[r.data,...p]); return r; };

  // Maintenance
  const addMxRequest    = async d => { const r = await supabase.from('maintenance_requests').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setMxRequests(m=>[{...r.data, maintenance_updates:[]},...m]); return r; };
  const updateMxRequest = async (id,d) => { const r = await supabase.from('maintenance_requests').update(d).eq('id',id).select().single(); if(!r.error) setMxRequests(m=>m.map(x=>x.id===id?{...x,...r.data}:x)); return r; };

  // Messages — sender_role is passed in d, not overridden here
  const addMessage = async d => { const r = await supabase.from('messages').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setMessages(m=>[r.data,...m]); return r; };

  // Documents
  const addDocument    = async d => { const r = await supabase.from('documents').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setDocuments(docs=>[r.data,...docs]); return r; };
  const deleteDocument = async id => { const r = await supabase.from('documents').delete().eq('id',id); if(!r.error) setDocuments(docs=>docs.filter(x=>x.id!==id)); return r; };

  // Expenses
  const addExpense    = async d => { const r = await supabase.from('expenses').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setExpenses(e=>[r.data,...e]); return r; };
  const deleteExpense = async id => { const r = await supabase.from('expenses').delete().eq('id',id); if(!r.error) setExpenses(e=>e.filter(x=>x.id!==id)); return r; };

  // Vacancies
  const addVacancy    = async d => { const r = await supabase.from('vacancies').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setVacancies(v=>[r.data,...v]); return r; };
  const updateVacancy = async (id,d) => { const r = await supabase.from('vacancies').update(d).eq('id',id).select().single(); if(!r.error) setVacancies(v=>v.map(x=>x.id===id?r.data:x)); return r; };

  // Late fees
  const addLateFee = async d => { const r = await supabase.from('late_fees').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setLateFees(lf=>[r.data,...lf]); return r; };

  // Checklists
  const addChecklist    = async d => { const r = await supabase.from('checklists').insert({...d, landlord_id:uid}).select().single(); if(!r.error) setChecklists(c=>[r.data,...c]); return r; };
  const updateChecklist = async (id,d) => { const r = await supabase.from('checklists').update(d).eq('id',id).select().single(); if(!r.error) setChecklists(c=>c.map(x=>x.id===id?r.data:x)); return r; };

  return {
    properties, tenants, leases, payments, mxRequests, messages, documents,
    expenses, vacancies, lateFees, checklists,
    loading, tier, reload: loadAll,
    addProperty, updateProperty, addTenant, updateTenant, addLease,
    addPayment, addMxRequest, updateMxRequest, addMessage,
    addDocument, deleteDocument,
    addExpense, deleteExpense,
    addVacancy, updateVacancy,
    addLateFee,
    addChecklist, updateChecklist,
    updateSubscription,
  };
}
